import { ref } from 'vue'

export type ConnectivityOptions = {
  /** Reaches the API and resolves true when it answered. */
  probe: () => Promise<boolean>
  /** Fired on each transition into online — the queue flushes here. */
  onOnline?: () => void
  onOffline?: () => void
  /** The clock, for tests. */
  now?: () => number
  /**
   * Where the moment the connection went is kept, so a page reloaded with no
   * signal still counts from then. Null keeps it in memory only.
   */
  store?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
}

const OFFLINE_SINCE_KEY = 'dockautomation.offlineSince'

function defaultStore(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/**
 * Tracks whether the API is actually reachable.
 *
 * `navigator.onLine` is not that: it reports "online" for a tablet associated
 * to an access point with no route to the API, for a captive portal, and for a
 * perfectly good link to a down server. So the state here moves on EVIDENCE —
 * the outcome of real requests, and an explicit probe of `/api/health/live`.
 * Browser events are demoted to hints that merely trigger a probe.
 */
export function createConnectivityMonitor(options: ConnectivityOptions) {
  const { probe, onOnline, onOffline } = options
  const now = options.now ?? Date.now
  const store = options.store === undefined ? defaultStore() : options.store

  // Optimistic to start: the operator should be able to work immediately, and
  // the first real request tells us the truth soon enough.
  const isOnline = ref(true)
  /**
   * When the connection went, or null while there is one. The offline limit
   * counts from here (utils/offlineRules.ts, Mark 2026-10-15). Kept on the
   * tablet: a page reloaded out of range has not had a signal since then either.
   */
  const offlineSince = ref<number | null>(readOfflineSince())

  function readOfflineSince(): number | null {
    try {
      const kept = Number(store?.getItem(OFFLINE_SINCE_KEY) ?? '')
      return Number.isFinite(kept) && kept > 0 ? kept : null
    } catch {
      return null
    }
  }

  function keepOfflineSince(value: number | null) {
    offlineSince.value = value
    try {
      if (value === null) store?.removeItem(OFFLINE_SINCE_KEY)
      else store?.setItem(OFFLINE_SINCE_KEY, String(value))
    } catch {
      // No storage: the count still runs for this page.
    }
  }
  const lastCheckedAt = ref<number | null>(null)
  let probing: Promise<boolean> | null = null

  function setOnline(next: boolean) {
    // The connection is judged per answer, and the first no-answer starts the count.
    if (next && offlineSince.value !== null) keepOfflineSince(null)
    if (!next && offlineSince.value === null) keepOfflineSince(now())
    if (isOnline.value === next) return
    isOnline.value = next
    if (next) onOnline?.()
    else onOffline?.()
  }

  /** A request reached the API — the strongest possible evidence. */
  function reportSuccess() {
    setOnline(true)
  }

  /** A request failed to get an answer. */
  function reportFailure() {
    setOnline(false)
  }

  /** Ask the API directly. Single-flight: callers share one in-flight probe. */
  async function check(): Promise<boolean> {
    if (probing) return probing
    probing = (async () => {
      try {
        const reachable = await probe()
        lastCheckedAt.value = Date.now()
        setOnline(reachable)
        return reachable
      } catch {
        lastCheckedAt.value = Date.now()
        setOnline(false)
        return false
      } finally {
        probing = null
      }
    })()
    return probing
  }

  /**
   * A browser `online` event (or similar). It is only a nudge to check sooner —
   * deliberately NOT a state change, because the browser is often wrong.
   */
  async function hint(): Promise<void> {
    await check()
  }

  return { isOnline, offlineSince, lastCheckedAt, reportSuccess, reportFailure, check, hint }
}

export type ConnectivityMonitor = ReturnType<typeof createConnectivityMonitor>
